// Single source of truth for the "Accept readiness card" chat message.
//
// Two surfaces send this exact text: the inline readiness card in the chat
// timeline (render/index.tsx) and the pinned readiness card on the IssuePage
// editor pane (IssuePage.tsx). The agent recognises this phrasing to drive
// `apply_update_issue`, so the two paths MUST not drift — keep the one
// builder here and have both call it.
export const buildReadinessAcceptMessage = (
  issueIid: number,
  acceptToken?: string,
): string =>
  `I accept the readiness card for issue #${issueIid}. ` +
  `Apply it now via apply_update_issue with the proposed card values` +
  `${acceptToken ? ` (acceptToken: ${acceptToken})` : ""}.`;

const ACCEPT_MESSAGE_RE = /^I accept the readiness card for issue #(\d+)\. /;

/**
 * The issue this text accepts, when it is {@link buildReadinessAcceptMessage}'s
 * output, else null.
 *
 * The message is protocol, not the user's words: an English instruction to the
 * agent carrying the card's acceptToken. It rides a plain user_message because
 * the agent recognises the phrasing, so every surface that renders the user's
 * turns renders it too — live on dev, "I accept the readiness card for issue
 * #147. Apply it now via apply_update_issue … (acceptToken: 1790176539019.4f43…)"
 * sat in the user's own bubble, in English, in German sessions (#145, #147,
 * #158). The bytes sent are unchanged; renderers show {@link acceptedDisplayKey}.
 */
export const acceptedIssueOf = (text: string): number | null => {
  const m = ACCEPT_MESSAGE_RE.exec(text);
  return m ? Number(m[1]) : null;
};

/** The catalog key a renderer shows in place of the accept message. */
export const acceptedDisplayKey = "chatRender.acceptedChanges" as const;

const REFINE_SEED_RE = /^Let's refine issue #(\d+): "/;

/**
 * The issue a refine SEED opens, when this text is one, else null.
 *
 * "Verfeinern starten" on the issue page (and Focus's refine start) sends
 * "Let's refine issue #N: "<title>". Ground in the codebase first, then ask me
 * the questions …" — an English instruction to the agent, which then sat in the
 * user's own bubble as if they had typed it, in German sessions too (#172,
 * #173). Like the accept message it is protocol: the bytes sent are unchanged,
 * and renderers show {@link refineSeedDisplayKey} instead.
 */
export const refineSeedIssueOf = (text: string): number | null => {
  const m = REFINE_SEED_RE.exec(text);
  return m ? Number(m[1]) : null;
};

/** The catalog key a renderer shows in place of a refine seed. */
export const refineSeedDisplayKey = "chatRender.refineStarted" as const;
